/**
 * FillFromRecordDialog — "New from template" for a template that is about a
 * record of another module (its `templateEntity`, e.g. zehn-hr:employee).
 *
 * The person picks the record (searched at that module through docs'
 * pass-through, as the person — templateSources.js), sees the values its
 * bound placeholders will get, may correct any of them, and creates the
 * document — instantiated with `values` and `sourceRef`, filled on the server
 * (backend docs/template-data.md). Values the module would not show them come
 * back `denied` and stay empty to type. Without that module (the install has
 * not turned it on, or it did not answer) the person types the values or
 * skips.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Lock, Search as SearchIcon, UserRound } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useGetRequest } from '@/shared/hooks/requests.js';
import { TEMPLATE_PLACEHOLDERS } from '@/shared/utils/urls.js';
import { resolveVariant } from '@/shared/utils/languages.js';
import {
  DOCS_BINDINGS, boundToSource, resolveSource, sourceLang, splitEntity,
} from '@/shared/utils/templateSources.js';

const INPUT = 'h-8 w-full min-w-0 rounded-[4px] border border-border bg-card px-2 text-sm text-foreground outline-none focus:border-brand';
const BTN = 'inline-flex h-8 items-center gap-1.5 rounded-[4px] border border-border bg-card px-3 text-sm font-medium text-foreground hover:bg-muted/60 disabled:opacity-50 cursor-pointer';
const BTN_PRIMARY = 'inline-flex h-8 items-center gap-1.5 rounded-[4px] bg-brand px-3 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 cursor-pointer';

/** The family member being instantiated (its placeholder rows carry the bindings). */
function memberOf(template, language, uiLanguage) {
  const variants = Array.isArray(template?.variants) ? template.variants : [];
  return variants.find((v) => v.language === language) || resolveVariant(template, uiLanguage) || template;
}

export function FillFromRecordDialog({ open, template, language, busy, onCancel, onCreate }) {
  const { t, i18n } = useTranslation(['my-docs']);
  const member = useMemo(() => memberOf(template, language, i18n.language), [template, language, i18n.language]);
  const lang = sourceLang(language || member?.language, i18n.language);
  const target = splitEntity(template?.templateEntity);

  const loadRows = useGetRequest({ showError: false });
  const [rows, setRows] = useState([]);
  const [api, setApi] = useState(null);
  const [reason, setReason] = useState(null); // not_enabled | failed
  const [entityLabel, setEntityLabel] = useState(target?.entity || '');
  const [sensitive, setSensitive] = useState(new Set());
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null); // {id, label, sublabel}
  const [values, setValues] = useState({});
  const [denied, setDenied] = useState(new Set());
  const [loadingValues, setLoadingValues] = useState(false);
  const searchSeq = useRef(0);

  // The member's placeholder rows, and the source's catalogue (entity label,
  // which fields are sensitive) — once per open.
  useEffect(() => {
    if (!open || !member?.uuid || !target) return undefined;
    let live = true;
    setRows([]); setValues({}); setDenied(new Set()); setPicked(null); setQuery(''); setResults([]);
    loadRows.request({ url: TEMPLATE_PLACEHOLDERS.replace('{file_uuid}', member.uuid) }).then(({ success, response }) => {
      if (live && success) setRows(Array.isArray(response?.items) ? response.items : []);
    });
    resolveSource(target.source).then(async ({ api: found, reason: why }) => {
      if (!live) return;
      setApi(found);
      setReason(why);
      if (!found) return;
      try {
        const catalogue = await found.catalogue(lang);
        const entity = (catalogue?.entities || []).find((e) => e.key === target.entity);
        if (!live || !entity) return;
        setEntityLabel(entity.label || target.entity);
        setSensitive(new Set((entity.fields || []).filter((f) => f.sensitive).map((f) => f.path)));
      } catch {
        if (live) { setApi(null); setReason('failed'); }
      }
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, member?.uuid, template?.templateEntity, lang]);

  // The picker: what the module lets this person see, searched as they type.
  useEffect(() => {
    if (!open || !api || !target) return undefined;
    const seq = ++searchSeq.current;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const found = await api.search(target.entity, query.trim());
        if (seq === searchSeq.current) setResults(Array.isArray(found) ? found : []);
      } catch {
        if (seq === searchSeq.current) setResults([]);
      } finally {
        if (seq === searchSeq.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, api, query]);

  const fillable = rows.filter((row) => !DOCS_BINDINGS.includes(row.binding));
  const hasRegistration = rows.some((row) => DOCS_BINDINGS.includes(row.binding));
  const bound = target ? boundToSource(rows, target.source) : [];

  const pick = async (record) => {
    setPicked(record);
    if (!api || !bound.length) return;
    setLoadingValues(true);
    try {
      const answer = await api.values(target.entity, record.id, [...new Set(bound.map((b) => b.path))], lang);
      const got = answer?.values || {};
      const refused = new Set(answer?.denied || []);
      setValues((prev) => {
        const next = { ...prev };
        for (const { key, path } of bound) next[key] = refused.has(path) ? '' : (got[path] ?? '');
        return next;
      });
      setDenied(new Set(bound.filter(({ path }) => refused.has(path)).map(({ key }) => key)));
    } catch {
      setReason('failed');
    } finally {
      setLoadingValues(false);
    }
  };

  const create = () => {
    const filled = Object.entries(values)
      .filter(([, value]) => String(value ?? '').trim() !== '')
      .map(([key, value]) => ({ key, value }));
    onCreate({
      values: filled,
      sourceRef: picked && target
        ? { source: target.source, entity: target.entity, id: String(picked.id), label: picked.label || null }
        : null,
    });
  };

  if (!target) return null;
  const pathOf = Object.fromEntries(bound.map(({ key, path }) => [key, path]));

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel?.(); }}>
      <DialogContent className="flex max-h-[85vh] w-full max-w-lg flex-col gap-0 overflow-hidden p-0" data-testid="fill-from-record">
        <DialogHeader className="border-b border-border px-4 py-3">
          <DialogTitle className="text-base font-semibold text-foreground">
            {t('my-docs:templates.fill.title', { source: target.source })}
          </DialogTitle>
          <p className="truncate text-xs text-muted-foreground">{member?.name || template?.name}</p>
        </DialogHeader>

        <div className="min-h-[160px] flex-1 space-y-3 overflow-y-auto px-4 py-3">
          {reason ? (
            <p className="rounded-[6px] border border-amber-500/40 bg-amber-500/5 p-2 text-xs text-amber-800 dark:text-amber-300" data-reason={reason}>
              {t(`my-docs:templates.fill.unavailable.${reason}`, { source: target.source })}
            </p>
          ) : (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">
                {t('my-docs:templates.fill.choose', { entity: entityLabel })}
              </p>
              <div className="relative">
                <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('my-docs:templates.fill.search')}
                  className={`${INPUT} pl-8`}
                />
              </div>
              <div className="max-h-44 overflow-y-auto rounded-[6px] border border-border">
                {searching && results.length === 0 ? (
                  <div className="flex h-12 items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-brand" /></div>
                ) : results.length === 0 ? (
                  <p className="p-3 text-center text-xs text-muted-foreground">{t('my-docs:templates.fill.noResults')}</p>
                ) : (
                  results.map((record) => (
                    <button
                      key={record.id}
                      type="button"
                      data-record-id={record.id}
                      onClick={() => pick(record)}
                      className={`flex w-full items-center gap-2 border-b border-border/60 px-3 py-2 text-left last:border-b-0 hover:bg-muted/40 cursor-pointer ${picked?.id === record.id ? 'bg-brand/10' : ''}`}
                    >
                      <UserRound className="h-4 w-4 flex-none text-muted-foreground" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-foreground">{record.label}</span>
                        {record.sublabel && <span className="block truncate text-[11px] text-muted-foreground">{record.sublabel}</span>}
                      </span>
                      {picked?.id === record.id && <Check className="h-4 w-4 flex-none text-brand" />}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {fillable.length > 0 && (
            <div className="space-y-2">
              <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                {t('my-docs:templates.fill.values')}
                {loadingValues && <Loader2 className="h-3 w-3 animate-spin text-brand" />}
              </p>
              {fillable.map((row) => (
                <label key={row.key} className="block text-xs text-muted-foreground" data-key={row.key}>
                  <span className="flex items-center gap-1.5">
                    <span className="font-medium text-foreground">{row.label}</span>
                    {row.required && <span className="text-red-500">*</span>}
                    {sensitive.has(pathOf[row.key]) && (
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-500/10 px-1.5 text-[10px] font-semibold text-amber-700">
                        <Lock className="h-2.5 w-2.5" /> {t('my-docs:templates.fill.sensitive')}
                      </span>
                    )}
                  </span>
                  <input
                    className={INPUT}
                    value={values[row.key] ?? ''}
                    onChange={(e) => setValues((prev) => ({ ...prev, [row.key]: e.target.value }))}
                  />
                  {denied.has(row.key) && (
                    <span className="block pt-0.5 text-[10.5px]">{t('my-docs:templates.fill.denied')}</span>
                  )}
                </label>
              ))}
            </div>
          )}
          {hasRegistration && (
            <p className="text-[11px] text-muted-foreground">{t('my-docs:templates.fill.registrationNote')}</p>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border px-4 py-2">
          <button type="button" className={BTN} onClick={onCancel} disabled={busy}>
            {t('my-docs:templates.fill.cancel')}
          </button>
          <button type="button" className={BTN} onClick={() => onCreate({ values: [], sourceRef: null })} disabled={busy}>
            {t('my-docs:templates.fill.skip')}
          </button>
          <button type="button" className={BTN_PRIMARY} onClick={create} disabled={busy || loadingValues}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {t('my-docs:templates.fill.create')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
